/**
 * Batch Information Value Object
 * Contains lot and reference information for Microarray/MLPA/TripletExpansion libraries
 */
export interface BatchInfo {
  /** Lot number */
  readonly lot: string | null;

  /** Reference number */
  readonly reference: string | null;

  /** Position in the batch (optional) */
  readonly position: string | null;
}

/**
 * Creates a BatchInfo object, returning null if all fields are empty
 *
 * @param lot - Lot number
 * @param reference - Reference number
 * @param position - Position in batch
 * @returns BatchInfo object or null if all fields are empty
 */
export function createBatchInfo(
  lot?: string | null,
  reference?: string | null,
  position?: string | null
): BatchInfo | null {
  const normalizedLot = lot?.trim() ?? null;
  const normalizedReference = reference?.trim() ?? null;
  const normalizedPosition = position?.trim() ?? null;

  if (!normalizedLot && !normalizedReference && !normalizedPosition) {
    return null;
  }

  return {
    lot: normalizedLot,
    reference: normalizedReference,
    position: normalizedPosition
  };
}

/**
 * Checks if two BatchInfo objects are equal
 */
export function batchInfoEquals(
  a: BatchInfo | null,
  b: BatchInfo | null
): boolean {
  if (a === null && b === null) {
    return true;
  }
  if (a === null || b === null) {
    return false;
  }
  return (
    a.lot === b.lot && a.reference === b.reference && a.position === b.position
  );
}

/**
 * Partial BatchInfo for PATCH semantics.
 * Fields that are `undefined` are NOT included in the request (backend preserves existing value).
 * Fields that are `null` or `string` ARE included in the request (backend updates the value).
 */
export interface PatchBatchInfo {
  /** Lot number - undefined means "do not update" */
  readonly lot?: string | null;

  /** Reference number - undefined means "do not update" */
  readonly reference?: string | null;

  /** Position - undefined means "do not update" */
  readonly position?: string | null;
}

/**
 * Creates a PatchBatchInfo object for PATCH operations.
 * Preserves `undefined` for fields that should not be updated.
 *
 * @param lot - Lot number (undefined = don't update, null/"" = clear, string = set value)
 * @param reference - Reference number (undefined = don't update, null/"" = clear, string = set value)
 * @param position - Position (undefined = don't update, null/"" = clear, string = set value)
 * @returns PatchBatchInfo object or undefined if no fields to update
 */
export function createPatchBatchInfo(
  lot?: string | null,
  reference?: string | null,
  position?: string | null
): PatchBatchInfo | undefined {
  // If all fields are undefined, there's nothing to patch
  if (lot === undefined && reference === undefined && position === undefined) {
    return undefined;
  }

  // Build object with only the fields that should be updated
  // Using spread syntax to avoid readonly assignment issues
  return {
    ...(lot !== undefined && { lot: lot?.trim() || null }),
    ...(reference !== undefined && { reference: reference?.trim() || null }),
    ...(position !== undefined && { position: position?.trim() || null })
  };
}
